// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CancelOutlined,
  InfoOutlined,
  KeyboardReturnOutlined,
  KeyboardTabOutlined,
  Launch,
  LocalShippingOutlined,
  PendingOutlined,
  QuestionMarkRounded,
} from '@/constants/hardfin-standard-icons';
import {
  ShippingCarrier,
  getShippingCarrierLabel,
  getTrackingURLForShippingCarrier,
} from '@/constants/shipping-carrier';
import { TrackingData, TrackingItem } from '@/tracking/api/types';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

interface Props {
  data: TrackingData;
  compact?: boolean;
  hideHeader?: boolean;
  /**
   * When true, the component renders as a flowing block instead of a
   * self-contained scroll container. Use when an ancestor already provides
   * a scroll context that should include this timeline.
   */
  embedded?: boolean;
}

function getStatusIcon(status: string, compact?: boolean): ReactNode {
  const fontSize = compact ? 'small' : 'medium';
  const color = compact ? 'primary' : undefined;
  switch (status.toLowerCase()) {
    case 'transit':
      return <LocalShippingOutlined fontSize={fontSize} color={color} />;
    case 'failure':
      return <CancelOutlined fontSize={fontSize} color={color} />;
    case 'delivered':
      return <KeyboardTabOutlined fontSize={fontSize} color={color} />;
    case 'returned':
      return <KeyboardReturnOutlined fontSize={fontSize} color={color} />;
    case 'pre_transit':
      return <PendingOutlined fontSize={fontSize} color={color} />;
    default:
      return <QuestionMarkRounded fontSize={fontSize} color={color} />;
  }
}

function getDisplayStatus(status: string): string {
  switch (status.toLowerCase()) {
    case 'transit':
      return 'In transit';
    case 'failure':
      return 'Failed';
    case 'delivered':
      return 'Delivered';
    case 'returned':
      return 'Returned';
    case 'pre_transit':
      return 'Pre-transit';
    default:
      return 'Unknown';
  }
}

function formatLocation(event: TrackingItem): string | null {
  if (!event.location) return null;
  const parts = [
    event.location.city,
    event.location.state,
    event.location.zip,
    event.location.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function CompactTrackingEvent(props: {
  event: TrackingItem;
  index: number;
  total: number;
}) {
  const { event, index, total } = props;
  const displayStatus = getDisplayStatus(event.status);
  const icon = getStatusIcon(event.status, true);
  const location = formatLocation(event);
  const hasTooltip = Boolean(event.statusText);

  const tooltipContent = event.statusText ? (
    <Typography variant="caption">{event.statusText}</Typography>
  ) : null;

  const dateContent = (
    <TimelineOppositeContent
      sx={{
        flex: 0,
        minWidth: '90px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        paddingY: '4px',
        paddingLeft: 0,
        paddingRight: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {event.reportedAt.toFormat('MMM d, yyyy')}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {event.reportedAt.toFormat('h:mm a')}
      </Typography>
    </TimelineOppositeContent>
  );

  const separator = (
    <TimelineSeparator>
      {index > 0 ? (
        <TimelineConnector sx={{ flexGrow: 1, marginBottom: '4px' }} />
      ) : (
        <Box sx={{ flexGrow: 1 }} />
      )}
      <TimelineDot
        variant="outlined"
        color="primary"
        sx={{ padding: '4px', margin: 0 }}
      >
        {icon}
      </TimelineDot>
      {index < total - 1 ? (
        <TimelineConnector sx={{ flexGrow: 1, marginTop: '4px' }} />
      ) : (
        <Box sx={{ flexGrow: 1 }} />
      )}
    </TimelineSeparator>
  );

  const statusContent = (
    <TimelineContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        paddingY: '4px',
        paddingX: 1,
      }}
    >
      <Typography variant="body2" noWrap>
        <b>{displayStatus}</b>
      </Typography>
      {location && (
        <Typography variant="caption" color="text.secondary">
          {location}
        </Typography>
      )}
    </TimelineContent>
  );

  const rowContent = (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        alignItems: 'stretch',
        cursor: hasTooltip ? 'help' : 'default',
      }}
    >
      {dateContent}
      {separator}
      {statusContent}
    </Box>
  );

  return (
    <TimelineItem
      sx={{
        minHeight: 56,
        alignItems: 'stretch',
        '&:before': { display: 'none' },
      }}
    >
      {hasTooltip ? (
        <Tooltip title={tooltipContent} arrow placement="left">
          {rowContent}
        </Tooltip>
      ) : (
        rowContent
      )}
    </TimelineItem>
  );
}

function TrackingEvent(props: {
  event: TrackingItem;
  index: number;
  total: number;
}) {
  const { event, index, total } = props;
  const displayStatus = getDisplayStatus(event.status);
  const icon = getStatusIcon(event.status);
  const location = formatLocation(event);

  return (
    <TimelineItem>
      <TimelineOppositeContent
        sx={{
          flex: 0,
          width: '130px',
          minWidth: '130px',
        }}
        align="right"
        alignContent="center"
        variant="body2"
        color="text.secondary"
      >
        {event.reportedAt.toFormat('MMM d, yyyy')}
        <br />
        {event.reportedAt.toFormat('h:mm a')}
      </TimelineOppositeContent>
      <TimelineSeparator>
        {index > 0 && <TimelineConnector />}
        <TimelineDot color="primary">{icon}</TimelineDot>
        {index < total - 1 && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent sx={{ paddingY: '12px', paddingX: 2 }}>
        <Typography>
          <b>{displayStatus}</b>
        </Typography>
        {location && (
          <Typography variant="caption" color="text.secondary">
            {location}
          </Typography>
        )}
        <Typography variant="body2">{event.statusText}</Typography>
      </TimelineContent>
    </TimelineItem>
  );
}

/**
 * Displays carrier tracking history as a timeline.
 * Shows carrier link and list of tracking events with status, location, and timestamp.
 *
 * @param compact - When true, renders a condensed timeline with tooltips for details
 */
export function TrackingHistory(props: Props) {
  const { data, compact = false, hideHeader = false, embedded = false } = props;

  const carrier = data.trackingNumberDetails?.carrier;
  const trackingNumber = data.trackingNumberDetails?.trackingNumber;
  if (!carrier || !trackingNumber) {
    return null;
  }

  const carrierName =
    getShippingCarrierLabel(carrier as ShippingCarrier) ?? carrier;
  const carrierLink = getTrackingURLForShippingCarrier(
    carrier as ShippingCarrier,
    trackingNumber
  );

  const EventComponent = compact ? CompactTrackingEvent : TrackingEvent;

  const header = !hideHeader && (
    <Stack spacing={compact ? 0.5 : 1} flexShrink={0}>
      {carrierLink ? (
        <Link href={carrierLink} target="_blank">
          <Stack alignItems="center" direction="row" gap={0.5}>
            <Typography variant="caption">
              {carrierName} {trackingNumber}
            </Typography>
            <Launch fontSize="small" />
          </Stack>
        </Link>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {carrierName} {trackingNumber}
        </Typography>
      )}
    </Stack>
  );

  const body =
    data.shippingHistory.length > 0 ? (
      <Timeline
        sx={
          compact
            ? {
                padding: 0,
                margin: 0,
                width: '100%',
                '& .MuiTimelineItem-root:before': {
                  flex: 0,
                  padding: 0,
                },
              }
            : undefined
        }
      >
        {data.shippingHistory.map((event, index) => (
          <EventComponent
            key={event.key}
            event={event}
            index={index}
            total={data.shippingHistory.length}
          />
        ))}
      </Timeline>
    ) : carrier.toLowerCase() === 'other' ? (
      <Typography variant="body2" color="text.secondary">
        Carrier tracking is not supported for "Other" carrier
      </Typography>
    ) : (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          Waiting to receive tracking details from carrier (can take several
          minutes)
        </Typography>
        <Tooltip
          arrow
          placement="bottom"
          title="Tracking details only sync for known carriers with valid tracking numbers"
        >
          <InfoOutlined
            fontSize="small"
            color="action"
            sx={{ cursor: 'help' }}
          />
        </Tooltip>
      </Stack>
    );

  const bodyMarginTop = !hideHeader ? (compact ? 1 : 2) : 0;

  if (embedded) {
    return (
      <Box width="100%">
        {header}
        <Box width="100%" marginTop={bodyMarginTop}>
          {body}
        </Box>
      </Box>
    );
  }

  return (
    <Stack height="100%" overflow="hidden">
      {header}
      <Box width="100%" flex={1} overflow="auto" marginTop={bodyMarginTop}>
        {body}
      </Box>
    </Stack>
  );
}
